/* Code was generated using AI assistance */
import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { HiOutlineCog, HiOutlineCube, HiOutlineMagnifyingGlass } from "react-icons/hi2";
import { useNavigationAccess } from "../navigation/useNavigationAccess";
import { SETTINGS_MENU_ITEMS_RAW } from "../navigation/navConfig";
import { filterNavItems } from "../navigation/navAccess";
import { filterLauncherEntriesByQuery } from "../navigation/launcherFilter";
import "./SettingsLauncherDropdown.css";

const SettingsLauncherDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const { ctx, featuresLoading } = useNavigationAccess();

  const items = useMemo(() => {
    // Support global settings access regardless of current app scope
    const globalCtx = { ...ctx, navAppScopeActive: false };
    const filtered = filterNavItems(SETTINGS_MENU_ITEMS_RAW, globalCtx);
    
    // Sort alphabetically by label
    return [...filtered].sort((a, b) => 
      (a.label || "").toLowerCase().localeCompare((b.label || "").toLowerCase())
    );
  }, [ctx, featuresLoading]);

  const filteredItems = useMemo(
    () => filterLauncherEntriesByQuery(items, searchQuery, { includeGroup: false }),
    [items, searchQuery],
  );

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      return;
    }
    const t = window.setTimeout(() => searchInputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleEsc = (event) => {
      if (event.key !== "Escape") return;
      if (searchQuery.trim()) {
        event.preventDefault();
        setSearchQuery("");
        return;
      }
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEsc);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [isOpen, searchQuery]);

  return (
    <div className="settings-launcher-dropdown" ref={dropdownRef}>
      <button
        type="button"
        className={`settings-launcher-btn ${isOpen ? "is-active" : ""}`}
        onClick={() => setIsOpen((o) => !o)}
        aria-label="Settings"
        aria-expanded={isOpen}
        title="Settings"
      >
        <HiOutlineCog aria-hidden />
      </button>

      {isOpen && (
        <div className="settings-launcher-panel" role="dialog" aria-label="Settings navigation">
          <div className="settings-launcher-panel-top">
            <div className="settings-launcher-header">
              <h4>Settings</h4>
              <Link
                to="/home"
                className="settings-launcher-explore"
                onClick={() => setIsOpen(false)}
              >
                Go to Home
              </Link>
            </div>
            <label className="settings-launcher-search">
              <HiOutlineMagnifyingGlass className="settings-launcher-search-icon" aria-hidden />
              <input
                ref={searchInputRef}
                type="search"
                className="settings-launcher-search-input"
                placeholder="Search settings…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search settings"
                autoComplete="off"
                spellCheck="false"
              />
            </label>
          </div>

          <div className="settings-launcher-list">
            {featuresLoading ? (
              <div className="settings-launcher-loading">Loading…</div>
            ) : items.length === 0 ? (
              <div className="settings-launcher-empty">No settings available for your account.</div>
            ) : filteredItems.length === 0 ? (
              <div className="settings-launcher-empty">No matching settings.</div>
            ) : (
              filteredItems.map((item) => {
                const Icon = item.icon || HiOutlineCube;
                return (
                  <Link
                    key={item.key}
                    to={item.path}
                    className="settings-launcher-row"
                    onClick={() => setIsOpen(false)}
                  >
                    <span className="settings-launcher-row-icon" aria-hidden>
                      <Icon />
                    </span>
                    <span className="settings-launcher-row-label">{item.label}</span>
                  </Link>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsLauncherDropdown;
